'use client';

import { SmallLotPricing } from '@ravago/shared/page-data/models/elements';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { Icon } from '@ravago/shared/radiance/components/icon/components/Icon/Icon';
import { Link } from '@ravago/shared/radiance/components/link/components/Link/Link';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useTranslations } from '@ravago/shared/translations/client';

import { CombinedQuoteQuantity, CombinedResolution } from '../../../../../utils/quote-pricing/combined-pricing.utils';
import { getQuoteSelectionMeta } from '../../../../../utils/quote-pricing/quote-selection.utils';

import { QuoteLinesList } from '../../../../shared/quote-pricing/quote-lines-list';
import { SmallLotQuantityBreaks } from './small-lot-quantity-breaks';

interface Props {
   activeTab: string;
   hasSmallLot: boolean;
   isBracketPricing: boolean;
   smallLotPricingForColorGroup: SmallLotPricing | null;
   smallLotHasMtsLocal?: boolean;
   resolution: CombinedResolution;
   onRequestQuote: () => void;
   quoteQuantities: CombinedQuoteQuantity[];
   quoteLineMtsLocalMap: Record<string, boolean>;
   selectQuantity: (quantity: number, quoteLineId?: string) => void;
}

export const PricingContent = ({
   activeTab,
   hasSmallLot,
   isBracketPricing,
   smallLotPricingForColorGroup,
   smallLotHasMtsLocal,
   resolution,
   onRequestQuote,
   quoteQuantities,
   quoteLineMtsLocalMap,
   selectQuantity
}: Props) => {
   const translate = useTranslations();
   const quoteMeta = getQuoteSelectionMeta(resolution);

   if (activeTab === 'list-prices' && hasSmallLot && smallLotPricingForColorGroup) {
      const isSmallLotResolution = resolution.kind === 'small-lot';
      return (
         <SmallLotQuantityBreaks
            tiers={smallLotPricingForColorGroup.tiers}
            packaging={smallLotPricingForColorGroup.packaging}
            selectedQuantity={isSmallLotResolution ? resolution.quantity : 0}
            isQuantityValid={isSmallLotResolution}
            hasMtsLocal={smallLotHasMtsLocal}
            onRequestQuote={onRequestQuote}
         />
      );
   }

   return (
      <Box direction="column" gap="sm">
         <QuoteLinesList
            quoteQuantities={quoteQuantities}
            selectedLineId={quoteMeta.selectedLineId}
            selectedQuantity={quoteMeta.selectedQuantity}
            selectedLinePackaging={quoteMeta.selectedLinePackaging}
            isQuantityValid={quoteMeta.isQuantityValid}
            isSelectable={!isBracketPricing}
            leadTimeByLineId={quoteLineMtsLocalMap}
            onSelect={selectQuantity}
         />
         <Box justify="center" items="center" gap="xs">
            <Icon icon="info-circle" color="subtle" size="small" />
            <Typography component="span" variant="small" color="subtle">
               {translate('dynamic-product-block.need-different-packaging-or-quantity')}
            </Typography>
            <Typography component="span" variant="small" color="primary" underline>
               <Link onClick={onRequestQuote}>{translate('dynamic-product-block.small-lot.request-quote')}</Link>
            </Typography>
         </Box>
      </Box>
   );
};
